import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Download, FileText, Image as ImageIcon, RefreshCw, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Sessions() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/feas-sessions`);
      setSessions(res.ok ? await res.json() : []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = sessions.filter(s => {
    if (!search) return true;
    const q = search.toLowerCase();
    return [s.sponsor, s.protocol_study_name, s.pi_name, s.indication]
      .some(v => (v || '').toLowerCase().includes(q));
  });

  const fmt = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleString(); } catch { return s; } };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']">Feasibility Sessions</h1>
          <p className="text-slate-600 mt-1">Every captured feasibility — page screenshots, notes, and the generated DOCX summary.</p>
        </div>
        <Button variant="outline" onClick={load}><RefreshCw className="w-4 h-4 mr-2" /> Refresh</Button>
      </div>

      <Card className="stryde-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>All sessions ({sessions.length})</CardTitle>
            <CardDescription>Started in the Chrome extension. Click View to see captured pages.</CardDescription>
          </div>
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search sponsor / PI / protocol" className="max-w-sm" />
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No sessions yet. Open the Chrome extension &raquo; <b>Start Feasibility</b> to begin one.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Started</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Sponsor</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Protocol</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">PI</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Indication</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Pages</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map(s => (
                    <tr key={s.id} className="hover:bg-slate-50">
                      <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{fmt(s.started_at)}</td>
                      <td className="px-3 py-3 font-medium text-slate-900">{s.sponsor || '-'}</td>
                      <td className="px-3 py-3 text-sm text-slate-600 max-w-[200px] truncate" title={s.protocol_study_name}>{s.protocol_study_name || '-'}</td>
                      <td className="px-3 py-3 text-sm text-slate-600">{s.pi_name || '-'}</td>
                      <td className="px-3 py-3 text-sm text-slate-600">{s.indication || '-'}</td>
                      <td className="px-3 py-3"><Badge>{(s.captures || []).length}</Badge></td>
                      <td className="px-3 py-3">
                        <Badge className={s.status === 'submitted'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200 border'
                          : 'bg-amber-50 text-amber-700 border-amber-200 border'}>
                          {s.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setViewing(s)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <a href={`${API_URL}/api/feas-sessions/${s.id}/summary`} target="_blank" rel="noreferrer">
                            <Button variant="ghost" size="icon" className="h-8 w-8" title="Download DOCX">
                              <Download className="w-4 h-4" />
                            </Button>
                          </a>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{viewing?.protocol_study_name || 'Feasibility session'}</DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                <div><span className="text-slate-500">Sponsor:</span> <b>{viewing.sponsor || '-'}</b></div>
                <div><span className="text-slate-500">PI:</span> <b>{viewing.pi_name || '-'}</b></div>
                <div><span className="text-slate-500">Indication:</span> <b>{viewing.indication || '-'}</b></div>
                <div><span className="text-slate-500">Phase:</span> <b>{viewing.phase || '-'}</b></div>
                <div><span className="text-slate-500">Started by:</span> <b>{viewing.started_by || '-'}</b></div>
                <div><span className="text-slate-500">Status:</span> <b>{viewing.status}</b></div>
              </div>
              {viewing.notes && (
                <div className="bg-slate-50 rounded-lg p-3 text-sm">
                  <div className="font-semibold mb-1">Notes</div>
                  <div className="whitespace-pre-wrap text-slate-700">{viewing.notes}</div>
                </div>
              )}
              <div>
                <h3 className="font-semibold mb-2 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4" /> Captured pages ({(viewing.captures || []).length})
                </h3>
                <div className="space-y-3">
                  {(viewing.captures || []).map((c, idx) => (
                    <div key={c.id} className="border rounded-lg p-3">
                      <div className="text-sm font-medium mb-1">{c.page_label || `Page ${idx + 1}`}</div>
                      {c.page_url && <div className="text-xs text-slate-500 truncate">{c.page_url}</div>}
                      {c.image_b64 && (
                        <img src={c.image_b64.startsWith('data:') ? c.image_b64 : `data:image/jpeg;base64,${c.image_b64}`}
                          alt={c.page_label} className="mt-2 w-full max-h-[400px] object-contain rounded border" />
                      )}
                      {c.notes && <div className="mt-2 text-sm text-slate-700">{c.notes}</div>}
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex gap-2">
                <a href={`${API_URL}/api/feas-sessions/${viewing.id}/summary`} target="_blank" rel="noreferrer">
                  <Button><Download className="w-4 h-4 mr-2" /> Download DOCX summary</Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
